import './App.css'

import * as amplitude from '@amplitude/analytics-browser'
import { Auth0Provider } from '@auth0/auth0-react'
import AmpliContextProvider from '@contexts/AmpliContextProvider'
import { useEnvConfig } from '@contexts/EnvConfigProvider'
import { datadogRum } from '@datadog/browser-rum'
import { useDataFederationApiUrl } from '@hooks/useApi'
import { CssBaseline } from '@mui/material'
import { ThemeProvider } from '@mui/material/styles'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Router } from '@router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import i18n from 'i18next'
import Backend from 'i18next-http-backend'
import { Suspense, useEffect, useMemo, useState } from 'react'
import { initReactI18next } from 'react-i18next'

import { Ampli, ampli } from './ampli'
import GetTheme from './Router/theme/theme'
import { CSSVariables } from './styles'

const lng = localStorage.getItem('lng') || 'en'

i18n
	// learn more: https://github.com/i18next/i18next-http-backend
	.use(Backend)
	// connect with React
	.use(initReactI18next)
	// for all options read: https://www.i18next.com/overview/configuration-options
	.init({
		lng: lng,
		fallbackLng: 'en',
		supportedLngs: ['en', 'de', 'fr'],
		returnNull: false,

		interpolation: {
			escapeValue: false, // not needed for react as it escapes by default
		},
	})
const App = () => {
	const [maybeAmpli, setMaybeAmpli] = useState<Ampli | undefined>()

	const envConfig = useEnvConfig()
	const dataFederationApiUrl = useDataFederationApiUrl()

	useEffect(() => {
		if (!envConfig) {
			return
		}

		// Datadog
		const {
			AMPLITUDE_API_KEY,
			AMPLITUDE_SERVER = '',
			DATADOG_FRONTEND_APPLICATION_ID,
			DATADOG_FRONTEND_CLIENT_TOKEN,
			COMMIT_HASH,
			DATADOG_ENVIRONMENT,
			DATADOG_SITE,
		} = envConfig

		if (DATADOG_FRONTEND_APPLICATION_ID && DATADOG_FRONTEND_CLIENT_TOKEN) {
			datadogRum.init({
				applicationId: DATADOG_FRONTEND_APPLICATION_ID,
				clientToken: DATADOG_FRONTEND_CLIENT_TOKEN,
				site: DATADOG_SITE || 'datadoghq.com',
				service: 'co2-ai-ecosystem-frontend',
				env: DATADOG_ENVIRONMENT,
				version: COMMIT_HASH,
				sessionSampleRate: 100,
				trackUserInteractions: true,
				defaultPrivacyLevel: 'mask-user-input',
				allowedTracingUrls: [dataFederationApiUrl],
			})
			datadogRum.startSessionReplayRecording()
		}

		if (AMPLITUDE_API_KEY) {
			// Amplitude, env equal to production is a tradeoff, as ampli/cli generates strange new config
			ampli.load({
				client: {
					apiKey: AMPLITUDE_API_KEY,
					configuration: {
						serverZone: AMPLITUDE_SERVER.includes('api.eu.')
							? amplitude.Types.ServerZone.EU
							: amplitude.Types.ServerZone.US,
					},
				},
				environment: 'production',
			})
			setMaybeAmpli(ampli)
		}
	}, [dataFederationApiUrl, envConfig])

	const authorizationParams = useMemo(
		() => ({
			audience: envConfig?.AUTH0_AUDIENCE,
			redirect_uri: window.location.origin,
		}),
		[envConfig?.AUTH0_AUDIENCE]
	)

	if (!envConfig) {
		return null
	}

	const queryClient = new QueryClient()

	const theme = GetTheme('dark')

	const { AUTH0_DOMAIN, AUTH0_CLIENT_ID } = envConfig

	return (
		<Auth0Provider
			domain={AUTH0_DOMAIN}
			clientId={AUTH0_CLIENT_ID}
			authorizationParams={authorizationParams}
			cacheLocation="localstorage"
			useRefreshTokens={true}
		>
			<Suspense fallback={null}>
				<AmpliContextProvider ampli={maybeAmpli}>
					<QueryClientProvider client={queryClient}>
						<ThemeProvider theme={theme}>
							<LocalizationProvider dateAdapter={AdapterDateFns}>
								<CssBaseline />
								<CSSVariables theme={theme.palette} />
								<Router />
							</LocalizationProvider>
						</ThemeProvider>
					</QueryClientProvider>
				</AmpliContextProvider>
			</Suspense>
		</Auth0Provider>
	)
}

export default App
